/*
 * Purpose: AutoBlock Flow Setup — per-flow secrets, OAuth, and connector readiness
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  CheckCircle2,
  KeyRound,
  Link2,
  Loader2,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import type { OutcomePlan } from '@/services/autoblocks/types';
import { auditPlanReadiness } from '@/services/autoblocks/planReadiness';
import { useSecretStore } from '@/stores/secretStore';
import {
  connectGmailOAuth,
  getGmailAccessToken,
  hasGoogleOAuthConfig,
} from '@/services/connectors/gmailOAuth';
import { createGmailInbox, checkWebhookServerHealth } from '@/services/connectors/gmailInbound';
import { ContextualError } from '@/components/errors/ContextualError';
import { toastContextualIssue } from '@/lib/contextualIssuePolicy';
import { VaultUnlockInline } from '@/components/vault/VaultUnlockInline';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { toast } from 'sonner';

interface PlanReadinessPanelProps {
  plan: OutcomePlan;
  onPlanUpdate: (plan: OutcomePlan) => void;
}

export function PlanReadinessPanel({ plan, onPlanUpdate }: PlanReadinessPanelProps) {
  const secrets = useSecretStore((s) => s.secrets);
  const isUnlocked = useSecretStore((s) => s.isUnlocked);
  const setSecret = useSecretStore((s) => s.setSecret);

  const report = useMemo(() => auditPlanReadiness(plan), [plan, secrets, isUnlocked]);

  const [draftSecrets, setDraftSecrets] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [lastError, setLastError] = useState<{ message: string; retry?: () => void } | null>(null);

  if (report.items.length === 0) return null;

  const pct =
    report.requiredTotal > 0
      ? Math.round((report.requiredReady / report.requiredTotal) * 100)
      : 100;

  const saveSecret = async (name: string) => {
    const val = draftSecrets[name]?.trim();
    if (!val) {
      toast.error('Paste a key value first', {
        action: {
          label: 'Focus field',
          onClick: () => document.getElementById(`readiness-secret-${name}`)?.focus(),
        },
      });
      return;
    }
    if (!isUnlocked) {
      toastContextualIssue('Unlock the vault above to save this key', {
        label: 'Focus unlock',
        onClick: () => document.getElementById('vault-inline-pass')?.focus(),
      });
      return;
    }
    try {
      await setSecret(name, val);
      setDraftSecrets((d) => ({ ...d, [name]: '' }));
      toast.success(`${name} saved to global vault`);
      onPlanUpdate(plan);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setLastError({
        message: msg,
        retry: () => void saveSecret(name),
      });
    }
  };

  const onConnectGmail = async () => {
    if (!hasGoogleOAuthConfig()) return;
    setBusy('gmail');
    setLastError(null);
    try {
      await connectGmailOAuth();
      toast.success('Gmail connected');
      onPlanUpdate(plan);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Gmail OAuth failed';
      setLastError({
        message: msg,
        retry: () => void onConnectGmail(),
      });
    } finally {
      setBusy(null);
    }
  };

  const onCreateInbox = async () => {
    setBusy('inbox');
    setLastError(null);
    try {
      const up = await checkWebhookServerHealth();
      if (!up) {
        setLastError({
          message: 'Inbound API is offline. Start the full dev stack with npm run dev (not dev:vite alone).',
          retry: () => void onCreateInbox(),
        });
        return;
      }
      const inbox = await createGmailInbox();
      onPlanUpdate({
        ...plan,
        gmailInboxId: inbox.inboxId,
        gmailPostUrl: inbox.postUrl,
        useGmailInbound: true,
      });
      toast.success('Edge inbox created');
    } catch (e) {
      setLastError({
        message: e instanceof Error ? e.message : 'Inbox creation failed',
        retry: () => void onCreateInbox(),
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card
      className={
        report.allRequiredReady
          ? 'border-ab-green-sea/40 bg-ab-green-mist/30 dark:bg-ab-green-forest/15'
          : 'border-amber-500/40 bg-amber-500/5'
      }
    >
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <ShieldCheck className="h-4 w-4 ui-icon" />
          <CardTitle className="text-base">{PRODUCT_TERMS.autoBlockFlowSetup}</CardTitle>
          <Badge variant={report.allRequiredReady ? 'default' : 'secondary'}>
            {report.requiredReady}/{report.requiredTotal} required
          </Badge>
        </div>
        <CardDescription className="text-xs">
          Setup for this {PRODUCT_TERMS.autoBlockFlow} only — checks vault secrets and connections
          these {PRODUCT_TERMS.blocks.toLowerCase()} need. Dry run still works without these; live
          runs need them complete.
        </CardDescription>
        {report.requiredTotal > 0 && (
          <Progress value={pct} className="h-1.5 mt-2" />
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <VaultUnlockInline />

        {lastError && (
          <ContextualError
            message={lastError.message}
            action={
              lastError.retry
                ? { label: 'Try again', onClick: lastError.retry }
                : { label: 'Open Setup', href: REMEDIATION.setup }
            }
            secondaryAction={
              lastError.retry ? { label: 'Open Setup', href: REMEDIATION.setup } : undefined
            }
          />
        )}

        <ul className="space-y-2">
          {report.items.map((item) => {
            const isReady = item.status === 'ready';
            return (
              <li
                key={item.id}
                className="rounded-md border bg-background/80 p-3 text-sm space-y-2"
              >
                <div className="flex items-start gap-2">
                  {isReady ? (
                    <CheckCircle2 className="h-4 w-4 text-ab-green-sea shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{item.label}</span>
                      {!item.required && (
                        <Badge variant="outline" className="text-[10px]">
                          optional
                        </Badge>
                      )}
                      <Badge
                        variant={isReady ? 'default' : 'secondary'}
                        className="text-[10px]"
                      >
                        {isReady ? 'ready' : 'needed'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                  </div>
                </div>

                {!isReady && item.kind === 'secret' && item.secretName && !isUnlocked && (
                  <ContextualError
                    message={`${item.secretName} is needed — unlock the vault to save it here.`}
                    action={{
                      label: 'Unlock vault',
                      onClick: () => document.getElementById('vault-inline-pass')?.focus(),
                    }}
                    secondaryAction={{ label: 'Open Setup', href: REMEDIATION.setup }}
                  />
                )}

                {!isReady && item.kind === 'secret' && item.secretName && isUnlocked && (
                  <div className="ui-field-action-group ui-field-action-group--stack sm:items-end pt-1">
                    <div className="flex-1 min-w-0">
                      <Label className="text-xs" htmlFor={`readiness-secret-${item.secretName}`}>
                        {item.secretName}
                      </Label>
                      <Input
                        id={`readiness-secret-${item.secretName}`}
                        type="password"
                        placeholder="Paste key…"
                        className="mt-1 text-xs font-mono"
                        value={draftSecrets[item.secretName] ?? ''}
                        onChange={(e) =>
                          setDraftSecrets((d) => ({ ...d, [item.secretName!]: e.target.value }))
                        }
                      />
                    </div>
                    <Button
                      size="sm"
                      className="ui-field-action-btn w-full sm:w-auto shrink-0"
                      onClick={() => void saveSecret(item.secretName!)}
                    >
                      <KeyRound className="h-3.5 w-3.5 mr-1" />
                      Save to vault
                    </Button>
                  </div>
                )}

                {!isReady && item.id === 'gmail_access' && (
                  <div className="space-y-2 pt-1">
                    {!hasGoogleOAuthConfig() && (
                      <ContextualError
                        message="GOOGLE_CLIENT_ID is missing — Gmail connect needs a Google OAuth Web client in your vault."
                        action={{ label: 'Add in Setup', href: REMEDIATION.setupGoogle }}
                        secondaryAction={{ label: 'Builder Secrets', href: REMEDIATION.builderSecrets }}
                      />
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => void onConnectGmail()}
                        disabled={busy !== null || !hasGoogleOAuthConfig()}
                      >
                        {busy === 'gmail' ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : (
                          <Link2 className="h-3.5 w-3.5 mr-1" />
                        )}
                        Connect Gmail
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => void onCreateInbox()}
                        disabled={busy !== null}
                      >
                        {busy === 'inbox' ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : null}
                        Create edge inbox
                      </Button>
                    </div>
                  </div>
                )}

                {!isReady && item.id === 'linkedin_oauth' && (
                  <div className="pt-1">
                    <ContextualError
                      message="LinkedIn is not connected — save app credentials if needed, then use Connect LinkedIn below."
                      action={{ label: 'LinkedIn Setup', href: REMEDIATION.setup }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {report.allRequiredReady && (
          <p className="text-xs text-ab-green-deep dark:text-ab-green-glow flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5" />
            All required credentials are set — you can dry run and approve for the builder.
          </p>
        )}

        {getGmailAccessToken() && (
          <p className="text-[10px] text-muted-foreground">
            Gmail token stored as GOOGLE_ACCESS_TOKEN (expires ~1h; reconnect before live runs).
          </p>
        )}
      </CardContent>
    </Card>
  );
}
